import React from 'react'
import { ArrowRight } from 'lucide-react'
import { HelpTooltip } from '@/components/HelpTooltip'
import { cn } from '@/lib/utils'
import { formatMoneySym } from '@/lib/format'
import { formatPoints } from '@/lib/plain-language'
import { currencySymbol } from '@/lib/calendar-utils'
import type { TargetMonthResult } from '@/types/shared'

/**
 * "Recent results" — the only backward-looking surface in the Target module.
 *
 * Everything else on this page argues about months that haven't happened. This one shows the
 * receipts: what each closed month was aiming at, where it actually landed, what the projection
 * said on the way, and what the engine did. That last pairing — "it said 76% at 30 days out and
 * the month closed at 75.8%" — is the single most convincing thing the product can say, and it
 * is worth nothing unless it is stated even when the answer is unflattering.
 *
 * NO CAUSAL ATTRIBUTION. There is no "the engine won you N room-nights" here and there must
 * never be: we have no counterfactual, so the number would be invented. See
 * ResolveTargetResultsMutation's header.
 *
 * BOTH SENTENCES ARE COMPOSED BY THE BACKEND and rendered verbatim. The locked vocabularies
 * (docs/target-vocabulary.md for "projected end-of-month", docs/revenue-vocabulary.md for
 * "final", "ADR", "pts") span both sides of the wire, and the plain-language convention test can
 * only see this side — so the wording lives where the numbers do.
 */

interface TargetResultsSectionProps {
  results: TargetMonthResult[]
  currency: string
  /** "Building ADR" at a combined inventory, whose final ADR covers every listing. Defaults to "ADR". */
  adrLabel?: string
  /** Seeds the page's draft in place — same helper the verdict CTAs use. No navigation. */
  onApplySuggestion: (value: number, month: number) => void
  animDelay?: number
}

export function TargetResultsSection({
  results,
  currency,
  adrLabel = 'ADR',
  onApplySuggestion,
  animDelay = 0,
}: TargetResultsSectionProps) {
  // No closed occurrence yet is the normal state for most of the fleet until a month actually
  // ends. An empty-state card would be a heading promising something the product cannot yet
  // show — so the section is simply absent.
  if (results.length === 0) return null

  const sym = currencySymbol(currency)

  return (
    <div
      className="animate-slide-in mt-6 overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm"
      style={{ animationDelay: `${animDelay}ms` }}
    >
      {/* "Recent results", NOT "Results so far this year". The evidence behind a row is the
          month's forecast snapshots, and those are pruned at the property's retention setting
          (90 days by default), so a January month is gone by May. A title claiming the year
          would be the exact defect this workstream exists to correct — a surface promising
          coverage the data cannot keep. The tooltip says how far back it reaches and why. */}
      <div className="flex items-start justify-between gap-4 border-b border-border/40 px-5 py-4">
        <div>
          <div className="flex items-center gap-1">
            <h2 className="text-sm font-semibold text-foreground">Recent results</h2>
            <HelpTooltip
              content="How the months you steered actually finished: the final occupancy from your own bookings, what the projection was saying on the way there, and what the engine did about it. A month appears once it has closed, and drops off again when the projection history behind it is cleared — so this reaches back a few months, not the whole year."
              learnMore="/guides/targets"
            />
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            How your last few closed months actually finished — measured from your bookings, not estimated.
          </p>
        </div>
        <p className="shrink-0 pt-0.5 text-xs text-muted-foreground">
          {results.length} month{results.length === 1 ? '' : 's'} closed
        </p>
      </div>

      <div className="divide-y divide-border/40">
        {results.map((row, idx) => (
          <ResultRow
            key={`${row.targetYear}-${row.targetMonth}`}
            row={row}
            sym={sym}
            adrLabel={adrLabel}
            onApplySuggestion={onApplySuggestion}
            animDelay={animDelay + 40 + idx * 40}
          />
        ))}
      </div>
    </div>
  )
}

interface ResultRowProps {
  row: TargetMonthResult
  sym: string
  adrLabel: string
  onApplySuggestion: (value: number, month: number) => void
  animDelay: number
}

function ResultRow({ row, sym, adrLabel, onApplySuggestion, animDelay }: ResultRowProps) {
  return (
    <div
      className="animate-slide-in grid gap-x-5 gap-y-3 px-5 py-4 transition-colors hover:bg-muted/20 md:grid-cols-[150px_215px_minmax(0,1fr)_auto] md:items-start"
      style={{ animationDelay: `${animDelay}ms` }}
    >
      {/* Month */}
      <div className="flex items-start gap-2.5">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-[11px] font-bold text-muted-foreground">
          {row.monthName.slice(0, 3)}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium leading-none text-foreground">{row.monthName}</p>
          <p className="mt-1.5 text-[11px] tabular-nums text-muted-foreground">{row.targetYear}</p>
        </div>
      </div>

      {/* Target → final. The final is the figure the row exists for, so it carries the weight. */}
      <div className="min-w-0">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-xs tabular-nums text-muted-foreground">Target {row.targetValue}%</span>
          <ArrowRight className="h-3 w-3 shrink-0 self-center text-muted-foreground/50" />
          {/* One decimal, matching the sentence beside it — the backend already rounded to 1dp
              and JSON drops the trailing zero, so this restores the display, it does not compute. */}
          <span className="text-base font-semibold tabular-nums text-foreground">
            {row.finalOccupancy.toFixed(1)}%
          </span>
          <LandedBadge landed={row.landed} />
        </div>
        <p className="mt-1 text-[11px] tabular-nums text-muted-foreground">
          {formatPoints(row.gapFinal)} · {adrLabel} {formatMoneySym(row.finalAdr, sym)}
        </p>
      </div>

      {/* The evidence, in the backend's own words. */}
      <div className="min-w-0 space-y-1">
        {row.forecastLine && (
          <p className="text-[11px] leading-relaxed text-muted-foreground">{row.forecastLine}</p>
        )}
        <p className="text-[11px] leading-relaxed text-muted-foreground">{row.engineLine}</p>
      </div>

      {/* The invitation. Targets are perpetual, so editing this month's row IS next year's target;
          the button seeds the draft in place and the operator still previews and deploys. Absent
          when the backend has no number to offer — a suggestion is a figure or it is nothing. */}
      <div className="md:pt-0.5">
        {row.suggestedNext != null && (
          <button
            type="button"
            onClick={() => onApplySuggestion(row.suggestedNext!, row.targetMonth)}
            className="whitespace-nowrap rounded-md bg-muted px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted-foreground/15"
          >
            Set next {row.monthName} to {row.suggestedNext}%
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * Worded, never symbolic. A ✗ against a month an operator worked on reads as a mark out of ten;
 * "Fell short" is the same fact without the blame. "Landed" is emerald because the month did what
 * was asked; "Fell short" is amber, not rose — a missed target is information, not a fault.
 */
function LandedBadge({ landed }: { landed: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold',
        landed ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700',
      )}
    >
      {landed ? 'Landed' : 'Fell short'}
    </span>
  )
}
